import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DocsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f5f7] to-white">
      <Helmet>
        <title>Documentation | Sortio</title>
        <meta name="description" content="Documentation for Sortio - AI-powered file organization tool" />
      </Helmet>
      
      <Header />
      
      <main className="container mx-auto px-4 py-20">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-[#1d1d1f] mb-10 text-center">Documentation</h1>
          
          <div className="backdrop-blur-xl bg-white/70 rounded-2xl shadow-sm border border-[#e0e0e5] p-8 mb-10">
            <h2 className="text-2xl font-semibold text-[#1d1d1f] mb-6">Available Documentation</h2>
            <ul className="space-y-4">
              <li>
                <Link
                  to="/docs/getting-started"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Getting Started</span>
                    <span className="text-sm text-[#86868b]">Core features and basic usage guide</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/spaces"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <rect x="3" y="3" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
                      <rect x="14" y="3" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
                      <rect x="3" y="14" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
                      <rect x="14" y="14" width="7" height="7" rx="1.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Spaces</span>
                    <span className="text-sm text-[#86868b]">Monitored folders with AI-powered auto-sorting and entity extraction</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link 
                  to="/docs/local-models" 
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V17.25a2.25 2.25 0 002.25 2.25h15a2.25 2.25 0 002.25-2.25v-3.414a2.25 2.25 0 00-.662-1.593l-6.416-6.416a2.25 2.25 0 00-1.592-.662h-.826M3 16.5h16.5m-16.5 0a1.5 1.5 0 01-1.5-1.5m1.5 1.5a1.5 1.5 0 001.5 1.5m-1.5-3h16.5m-16.5 0a1.5 1.5 0 00-1.5 1.5m1.5-1.5a1.5 1.5 0 011.5-1.5" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Offline Mode Setup</span>
                    <span className="text-sm text-[#86868b]">Run AI models locally for enhanced privacy</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/byok"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Bring Your Own Key</span>
                    <span className="text-sm text-[#86868b]">Use your own OpenAI or Anthropic API key with Sortio</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/connected-ai"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Connect Claude or ChatGPT</span>
                    <span className="text-sm text-[#86868b]">Let an AI assistant search your knowledge graph, read-only</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/clio-integration"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3.75l7.5 4.125v8.25L12 20.25l-7.5-4.125v-8.25L12 3.75zM12 12l7.5-4.125M12 12v8.25M12 12L4.5 7.875" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Clio Integration</span>
                    <span className="text-sm text-[#86868b]">File local documents into Clio matters with OCR, categories, and audit trails</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/prompt-engineering"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Prompt Engineering + Tips</span>
                    <span className="text-sm text-[#86868b]">Craft effective prompts for better sorting results</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/security"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Security & Privacy</span>
                    <span className="text-sm text-[#86868b]">Learn about our data integrity and protection measures</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/rules-engine"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Rules Engine</span>
                    <span className="text-sm text-[#86868b]">Create deterministic rules for predictable file organization</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/watch-folders-automations"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Watch Folders & Automations</span>
                    <span className="text-sm text-[#86868b]">Run automations on new files automatically, with one-click undo on every run <span className="ml-1 text-[10px] uppercase tracking-wide text-[#06c]">New in 8.18</span></span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/importing-rulepacks"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Importing Rulepacks</span>
                    <span className="text-sm text-[#86868b]">Import ready-made automation rules from the community Discover marketplace</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/file-safety"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Undo, Preview & File Safety</span>
                    <span className="text-sm text-[#86868b]">Preview before apply, full undo history, no overwrites, protected system folders</span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/language-settings"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Language Settings</span>
                    <span className="text-sm text-[#86868b]">6 interface languages plus a separate content language for AI folder names <span className="ml-1 text-[10px] uppercase tracking-wide text-[#06c]">New in 8.17</span></span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/finder-tags"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 6h.008v.008H6V6z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">macOS Finder Tag Automations</span>
                    <span className="text-sm text-[#86868b]">Match and write Finder tags from rule conditions and the TAG action <span className="ml-1 text-[10px] uppercase tracking-wide text-[#06c]">New in 8.11</span></span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
              <li>
                <Link
                  to="/docs/auto-tag-spaces"
                  className="text-[#06c] hover:text-[#0077e6] transition-colors font-medium flex items-center group"
                >
                  <div className="bg-[#06c]/10 p-2 rounded-xl mr-3 group-hover:bg-[#06c]/20 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.624L16.5 21.75l-.398-1.126a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.179-.398a2.25 2.25 0 001.423-1.423l.398-1.126.398 1.126a2.25 2.25 0 001.423 1.423l1.179.398-1.179.398a2.25 2.25 0 00-1.423 1.423z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block">Auto-Tag in Spaces</span>
                    <span className="text-sm text-[#86868b]">Build a tag corpus from a folder, then suggest and apply tags to untagged files <span className="ml-1 text-[10px] uppercase tracking-wide text-[#06c]">New in 8.11</span></span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-auto text-[#86868b] group-hover:text-[#06c] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </li>
            </ul>
          </div>
        
          
          <div className="mt-10 bg-[#f5f5f7] rounded-2xl p-8 border border-[#e0e0e5]">
            <div className="flex items-start">
              <div className="bg-[#06c] rounded-xl p-3 mr-5 shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-[#1d1d1f] mb-3">Documentation is Being Expanded</h2>
                <p className="text-[#424245] mb-4">
                  We're continuously improving our documentation. This is a living document that grows based on user needs and feedback.
                </p>
                <p className="text-[#424245] flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#06c] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    Have a question not covered here? Ask the Sortio Assistant in the chat bubble in the bottom right corner. We use AI to monitor frequently asked questions and regularly add those answers here to help all users.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocsPage;